/**
 * useRetryAndEditMessage - 消息重试和编辑 Hook
 * 提取自 ChatPage.tsx，包含 handleRetry 和 handleEditSave 函数
 */

import { useCallback, useRef } from 'react';
import { Message, Conversation } from '@/types';
import { rebuildMessagesWithVersionPath } from '@/services/conversationService';
import { createPendingId } from '@/utils/messageUtils';
import { useUIStore } from '@/stores/uiStore';

export interface AgentInfo {
  id: string;
  name: string;
  logo?: string;
}

export interface UseRetryAndEditMessageOptions {
  currentConversation: Conversation | undefined;
  currentConversationId: string | undefined;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  currentConversationAgent?: AgentInfo | null;
  selectedAgent?: AgentInfo | null;
}

export interface UseRetryAndEditMessageReturn {
  handleRetry: (message: Message) => Promise<void>;
  handleEditSave: (
    message: Message,
    newContent: string,
    fileIds?: string[]
  ) => Promise<void>;
  setHandleSendMessage: (
    fn: (
      content: string,
      isRetry?: boolean,
      options?: Record<string, any>
    ) => Promise<void>
  ) => void;
}

export function useRetryAndEditMessage({
  currentConversation,
  currentConversationId,
  setConversations,
  currentConversationAgent,
  selectedAgent,
}: UseRetryAndEditMessageOptions): UseRetryAndEditMessageReturn {
  const setConversationsRef = useRef(setConversations);
  setConversationsRef.current = setConversations;

  // 🔥 获取清除草稿的函数
  const setDraftMessage = useUIStore(state => state.setDraftMessage);

  // 使用 ref 存储 handleSendMessage，解决循环依赖问题
  const handleSendMessageRef = useRef<
    (
      content: string,
      isRetry?: boolean,
      options?: Record<string, any>
    ) => Promise<void>
  >(async () => {
    console.warn('handleSendMessage not yet initialized');
  });

  const setHandleSendMessage = useCallback(
    (
      fn: (
        content: string,
        isRetry?: boolean,
        options?: Record<string, any>
      ) => Promise<void>
    ) => {
      handleSendMessageRef.current = fn;
    },
    []
  );

  // 包装函数，使用 ref 调用
  const handleSendMessage = useCallback(
    async (
      content: string,
      isRetry?: boolean,
      options?: Record<string, any>
    ) => {
      return handleSendMessageRef.current(content, isRetry, options);
    },
    []
  );

  const handleRetry = async (message: Message) => {
    if (!currentConversation) {
      console.error('❌ No currentConversation');
      return;
    }

    const messages = currentConversation.messages || [];
    // 使用 currentConversation.id 作为真实的 conversationId
    // 避免使用可能过期的 currentConversationId
    const actualConversationId =
      currentConversation.id || currentConversationId;

    // 🔥 清除草稿记录
    if (actualConversationId) {
      setDraftMessage(actualConversationId, '');
    }

    // 🔥 重构：生成临时消息ID，用于匹配流式返回的内容
    const pendingId = createPendingId();

    // 对于retry，先尝试从当前消息的历史中提取content作为fallback
    // 这样即使treeData被prune，仍然能找到要发送的内容
    let fallbackContent = '';
    let fallbackFileIds: string[] = [];

    if (message.role === 'assistant') {
      // Assistant retry: 提取对应的user message content
      const possibleUserMessage =
        message.previousMessageId !== undefined
          ? messages.find(
              m =>
                String(m.id) === String(message.previousMessageId) ||
                m.backendId === message.previousMessageId
            )
          : messages.findLast(
              // 找到最近的一条user消息（作为最后的fallback）
              m => m.role === 'user' && m.timestamp <= message.timestamp
            );
      if (possibleUserMessage) {
        const activeChoiceIndex = possibleUserMessage.activeChoice ?? 0;
        const versionBlocks =
          possibleUserMessage.choices?.[activeChoiceIndex]?.blocks || [];
        const messageContentBlock: any =
          versionBlocks.find((block: any) => block.type === 'text') || {};
        fallbackContent = messageContentBlock?.content || '';
        fallbackFileIds = versionBlocks
          .filter((block: any) => block.type === 'file')
          .map((block: any) => block.fileId || block.content)
          .filter(Boolean);
      }
    } else if (message.role === 'user') {
      // User retry: 直接使用user message的content
      const activeChoiceIndex = message.activeChoice ?? 0;
      const versionBlocks = message.choices?.[activeChoiceIndex]?.blocks || [];
      const messageContentBlock: any =
        versionBlocks.find((block: any) => block.type === 'text') || {};
      fallbackContent = messageContentBlock?.content || '';
      fallbackFileIds = versionBlocks
        .filter((block: any) => block.type === 'file')
        .map((block: any) => block.fileId || block.content)
        .filter(Boolean);
    }

    // 在任何清理/变更 treeData 之前，先根据当前激活路径与消息类型预计算本次重试需要的内容与 lastMessageId
    let contentToSend = '';
    let fileIdsToSend: string[] = [];
    let lastMessageIdToSend: number | null = null;
    if (message.role === 'user') {
      // User retry: 使用user message的previousMessageId作为分支点
      const prevMessageId = message.previousMessageId ?? null;
      if (prevMessageId !== null && typeof prevMessageId !== 'number') {
        // previousMessageId 是临时ID，尝试从 treeData 找到真实 backendId
        const findAssistantBackendId = (
          nodes: any[],
          tempId: any
        ): number | null => {
          for (const node of nodes) {
            if (
              String(node.id) === String(tempId) &&
              node.role === 'assistant'
            ) {
              return typeof node.id === 'number' ? node.id : null;
            }
            if (node.children && node.children.length > 0) {
              const found = findAssistantBackendId(node.children, tempId);
              if (found !== null) return found;
            }
          }
          return null;
        };
        lastMessageIdToSend = currentConversation.treeData
          ? findAssistantBackendId(currentConversation.treeData, prevMessageId)
          : null;
      } else {
        lastMessageIdToSend = prevMessageId;
      }
      // 内容直接来自该 user message 当前激活版本
      const activeChoiceIndex = message.activeChoice ?? 0;
      const versionBlocks = message.choices?.[activeChoiceIndex]?.blocks || [];
      const messageContentBlock: any =
        versionBlocks.find((block: any) => block.type === 'text') || {};
      contentToSend = messageContentBlock?.content || '';
      fileIdsToSend = versionBlocks
        .filter((block: any) => block.type === 'file')
        .map((block: any) => block.fileId || block.content)
        .filter(Boolean);
    } else {
      // Assistant retry: 直接用 message.previousMessageId 搜索 user 节点
      // 而不是先找 assistant 节点（因为 assistant 节点可能不在 treeData 中）
      console.log(
        '🔍 [contentToSend] Searching for user message, previousMessageId:',
        message.previousMessageId
      );

      // 方法1：直接用 previousMessageId 在 treeData 中搜索 user 节点
      if (
        currentConversation.treeData &&
        message.previousMessageId !== undefined
      ) {
        const findUserNodeById = (nodes: any[], targetId: any): any | null => {
          for (const node of nodes) {
            if (String(node.id) === String(targetId) && node.role === 'user') {
              return node;
            }
            if (node.children && node.children.length > 0) {
              const found = findUserNodeById(node.children, targetId);
              if (found) return found;
            }
          }
          return null;
        };

        const userNode = findUserNodeById(
          currentConversation.treeData,
          message.previousMessageId
        );
        if (userNode) {
          console.log(
            '✅ [contentToSend] Found user node in treeData by previousMessageId:',
            userNode.id
          );
          const contentTxt = userNode.contents?.find(
            (it: any) => it.type === 'text'
          ) || { content: '' };
          contentToSend = contentTxt.content || '';
          fileIdsToSend = (userNode.contents || [])
            .filter((it: any) => it.type === 'file')
            .map((it: any) => it.fileId || it.content || it.id)
            .filter(Boolean);

          const userBId =
            typeof userNode.id === 'number'
              ? userNode.id
              : typeof userNode.backendId === 'number'
                ? userNode.backendId
                : null;
          if (userBId !== null) {
            lastMessageIdToSend = userBId;
          }
          console.log(
            '✅ [contentToSend] Content from treeData:',
            contentToSend?.substring(0, 50),
            'lastMessageId:',
            lastMessageIdToSend
          );
        }
      }

      // 方法2：如果 treeData 查找失败，在 messages 数组中用 previousMessageId 查找
      if (!contentToSend && message.previousMessageId !== undefined) {
        console.warn(
          '⚠️ [contentToSend] Tree lookup failed, trying messages array with previousMessageId'
        );
        const userMsg = messages.find(
          m =>
            m.role === 'user' &&
            (String(m.id) === String(message.previousMessageId) ||
              m.backendId === message.previousMessageId)
        );
        if (userMsg) {
          console.log(
            '✅ [contentToSend] Found user message in messages array by previousMessageId:',
            userMsg.id
          );
          const activeChoiceIndex = userMsg.activeChoice ?? 0;
          const versionBlocks =
            userMsg.choices?.[activeChoiceIndex]?.blocks || [];
          contentToSend =
            versionBlocks.find((b: any) => b.type === 'text')?.content || '';
          fileIdsToSend = versionBlocks
            .filter((b: any) => b.type === 'file')
            .map((b: any) => b.fileId || b.content || b.id)
            .filter(Boolean);
          if (typeof userMsg.backendId === 'number') {
            lastMessageIdToSend = userMsg.backendId;
          }
          console.log(
            '✅ [contentToSend] Content from messages:',
            contentToSend?.substring(0, 50),
            'lastMessageId:',
            lastMessageIdToSend
          );
        }
      }

      // 方法3：位置查找（找 assistant 前面的 user）
      if (!contentToSend) {
        console.warn(
          '⚠️ [contentToSend] previousMessageId lookup failed, trying position-based search'
        );
        let userMessage: Message | null | undefined = null;
        const assistantIndex = messages.findIndex(
          m => m.id === message.id || String(m.id) === String(message.id)
        );
        if (assistantIndex > 0) {
          for (let i = assistantIndex - 1; i >= 0; i--) {
            if (messages[i].role === 'user') {
              userMessage = messages[i];
              console.log(
                '✅ [contentToSend] Found user message by position:',
                userMessage.id
              );
              break;
            }
          }
        }

        // 4. 如果还是没找到，找最后一个 user 消息
        if (!userMessage) {
          userMessage = [...messages].reverse().find(m => m.role === 'user');
          if (userMessage) {
            console.log(
              '⚠️ [contentToSend] Using last user message as fallback:',
              userMessage.id
            );
          }
        }

        if (userMessage) {
          const userActiveChoiceIndex = userMessage.activeChoice ?? 0;
          const versionBlocks =
            userMessage.choices?.[userActiveChoiceIndex]?.blocks || [];
          contentToSend =
            versionBlocks.find((b: any) => b.type === 'text')?.content || '';
          fileIdsToSend = versionBlocks
            .filter((block: any) => block.type === 'file')
            .map((block: any) => block.fileId || block.content || block.id)
            .filter(Boolean);

          // 🔥 修复：同时更新 lastMessageIdToSend 为找到的 user 消息的 backendId
          if (typeof userMessage.backendId === 'number') {
            lastMessageIdToSend = userMessage.backendId;
          }
          console.log(
            '✅ [contentToSend] Content extracted:',
            contentToSend?.substring(0, 50),
            'lastMessageId:',
            lastMessageIdToSend
          );
        }
      } // 关闭 if (!contentToSend) 块
    }

    // 如果有 treeData，在树中添加新版本
    if (currentConversation.treeData) {
      setConversations(prev =>
        prev.map(conversation => {
          // 使用 actualConversationId 进行匹配，支持 temp-xxx 或真实 ID
          if (conversation.id !== actualConversationId) {
            return conversation;
          }
          // 深拷贝并在重试前清理无 backendId 的临时节点，避免它们被纳入版本链
          const pruneInvalidTreeNodes = (nodes: any[]): any[] => {
            return nodes
              .filter((node: any) => {
                const hasRealId = typeof node.id === 'number';
                const hasBackendIdProp = typeof node.backendId === 'number';
                return hasRealId || hasBackendIdProp;
              })
              .map((node: any) => {
                const newNode = { ...node };
                if (Array.isArray(node.children) && node.children.length > 0) {
                  newNode.children = pruneInvalidTreeNodes(node.children);
                }
                return newNode;
              });
          };
          const newTreeData = pruneInvalidTreeNodes(
            JSON.parse(JSON.stringify(conversation.treeData))
          );

          // 根据消息类型，在对应层添加新版本
          const findAndAddRetryVersion = (
            nodes: any[],
            targetBackendId: number | undefined,
            targetFrontendId: string | undefined,
            targetRole: 'user' | 'assistant',
            depth: number
          ): { success: boolean; retryDepth?: number } => {
            // 检查当前层是否包含目标节点
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];

              // 匹配逻辑：优先使用 backendId，其次使用 id 字符串化比较
              const isMatch =
                node.role === targetRole &&
                ((targetBackendId !== undefined &&
                  node.id === targetBackendId) ||
                  (targetFrontendId !== undefined &&
                    String(node.id) === String(targetFrontendId)));

              if (isMatch) {
                // 找到目标节点，根据角色类型添加新版本
                if (targetRole === 'user') {
                  // User message retry: 不在user层添加版本，而是在children层（assistant）添加新版本
                  // 直接在node.children数组开头添加新的assistant节点
                  // 确保previousMessageId是数字类型的backendId
                  const userBackendId =
                    typeof node.id === 'number' ? node.id : null;
                  if (userBackendId === null) {
                    console.error(
                      '❌ User node does not have a valid backendId for retry:',
                      node
                    );
                    return { success: false };
                  }
                  const newAssistantNode = {
                    id: pendingId, // 🔥 重构：使用 pendingId
                    role: 'assistant',
                    contents: [],
                    previousMessageId: userBackendId,
                    children: [],
                    isRetry: true,
                    isStreaming: true, // 立即设置为streaming状态
                  };
                  // 创建新的user节点，替换children（在开头添加新assistant）
                  const newUserNode = {
                    ...node,
                    children: [newAssistantNode, ...(node.children || [])],
                  };
                  // 返回新数组，替换当前节点
                  nodes[i] = newUserNode;
                  return { success: true, retryDepth: depth + 1 }; // assistant在下一层
                } else {
                  // Assistant message retry: 创建空内容的assistant节点
                  // 确保previousMessageId是数字类型的backendId
                  const prevMessageId =
                    typeof node.previousMessageId === 'number'
                      ? node.previousMessageId
                      : null;
                  if (prevMessageId === null) {
                    console.error(
                      '❌ Assistant node does not have a valid previousMessageId for retry:',
                      node
                    );
                    return { success: false };
                  }
                  const newAssistantNode = {
                    ...node,
                    id: pendingId, // 🔥 重构：使用 pendingId 作为 assistant 节点的 ID
                    contents: [], // 空内容，等待流式填充
                    previousMessageId: prevMessageId, // 显式设置为数字类型的previousMessageId
                    children: [], // 关键：children 为空数组，后续对话不会显示
                    isRetry: true,
                    isStreaming: true, // 立即设置为streaming状态
                  };
                  nodes.unshift(newAssistantNode);
                  return { success: true, retryDepth: depth };
                }
              }
            }

            // 如果当前层没找到，递归到每个节点的 children 中查找
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if (node.children && node.children.length > 0) {
                const result = findAndAddRetryVersion(
                  node.children,
                  targetBackendId,
                  targetFrontendId,
                  targetRole,
                  depth + 1
                );
                if (result.success) return result;
              }
            }

            return { success: false };
          };

          const result = findAndAddRetryVersion(
            newTreeData,
            message.backendId,
            String(message.id), // 🔥 重构：统一使用 message.id（转换为字符串）
            message.role,
            0
          );

          if (!result.success) {
            // 🔥 修复：当找不到目标节点时（可能是错误消息已被 prune），
            // 不要清空 treeData，而是尝试通过 lastMessageIdToSend 在已修剪的树中找到父 user 节点

            // 先尝试在 newTreeData 中通过 lastMessageIdToSend 找到 user 节点并添加 assistant
            if (
              lastMessageIdToSend !== null &&
              typeof lastMessageIdToSend === 'number' &&
              newTreeData.length > 0
            ) {
              const addAssistantToUserNode = (
                nodes: any[],
                targetUserId: number,
                depth: number
              ): { success: boolean; retryDepth?: number } => {
                for (let i = 0; i < nodes.length; i++) {
                  const node = nodes[i];
                  if (node.id === targetUserId && node.role === 'user') {
                    // 找到目标 user 节点，在其 children 开头添加新的 assistant 节点
                    const newAssistantNode = {
                      id: pendingId,
                      role: 'assistant',
                      contents: [],
                      previousMessageId: targetUserId,
                      children: [],
                      isRetry: true,
                      isStreaming: true,
                    };
                    node.children = [
                      newAssistantNode,
                      ...(node.children || []),
                    ];
                    return { success: true, retryDepth: depth + 1 };
                  }
                  if (node.children && node.children.length > 0) {
                    const fallbackSearchResult = addAssistantToUserNode(
                      node.children,
                      targetUserId,
                      depth + 1
                    );
                    if (fallbackSearchResult.success)
                      return fallbackSearchResult;
                  }
                }
                return { success: false };
              };

              const fallbackResult = addAssistantToUserNode(
                newTreeData,
                lastMessageIdToSend,
                0
              );

              if (fallbackResult.success) {
                // 成功在树中添加了新节点
                const fallbackVersionPath = {
                  ...conversation.versionPath,
                  [fallbackResult.retryDepth!]: 0,
                };

                // 清理 retryDepth 之后的所有 versionPath
                Object.keys(fallbackVersionPath).forEach(key => {
                  const keyDepth = parseInt(key, 10);
                  if (keyDepth > fallbackResult.retryDepth!) {
                    delete fallbackVersionPath[keyDepth];
                  }
                });

                const fallbackMessages = rebuildMessagesWithVersionPath(
                  newTreeData,
                  conversation.conversationId,
                  fallbackVersionPath
                );

                return {
                  ...conversation,
                  treeData: newTreeData,
                  versionPath: fallbackVersionPath,
                  messages: fallbackMessages,
                  isLoading: true,
                };
              }
            }

            // 如果上面的方法也失败了，回退到使用 messages 数组，但保留 newTreeData
            const messages = conversation.messages || [];
            let updatedMessages;

            if (message.role === 'user') {
              // User retry: 找到这个user message之后的所有assistant消息并清理
              const userMsgIndex = messages.findIndex(
                msg =>
                  msg.id === message.id ||
                  String(msg.id) === String(message.backendId)
              );

              if (userMsgIndex >= 0) {
                // 保留user message及之前的消息，清理user之后的所有assistant消息
                updatedMessages = messages.slice(0, userMsgIndex + 1);
              } else {
                // 找不到user message，保留所有消息
                updatedMessages = [...messages];
              }

              // 添加新的assistant消息
              const newAssistantMessage: Message = {
                id: pendingId,
                choices: [{ version: 0, blocks: [] }],
                role: 'assistant',
                timestamp: new Date(),
                isStreaming: true,
              };
              updatedMessages.push(newAssistantMessage);
            } else {
              // Assistant retry: 查找是否已有相同的 retry 占位消息（通过 message.id 匹配）
              // 如果有，替换它；如果没有，添加新的
              const existingRetryIndex = messages.findIndex(
                msg =>
                  msg.role === 'assistant' &&
                  (msg.id === message.id ||
                    String(msg.id) === String(message.backendId))
              );
              const newAssistantMessage: Message = {
                id: pendingId,
                choices: [{ version: 0, blocks: [] }],
                role: 'assistant',
                timestamp: new Date(),
                isStreaming: true,
              };

              if (existingRetryIndex >= 0) {
                // 替换已有的错误消息
                updatedMessages = messages.map((msg, idx) =>
                  idx === existingRetryIndex ? newAssistantMessage : msg
                );
              } else {
                // 添加新消息，保留有 backendId 的消息
                const pureMessages = messages.filter(it => !!it.backendId);
                updatedMessages = [...pureMessages, newAssistantMessage];
              }
            }

            return {
              ...conversation,
              treeData: newTreeData, // 🔥 关键修复：保留 newTreeData 而不是清空
              versionPath: conversation.versionPath || {}, // 保留现有的 versionPath
              messages: updatedMessages,
              isLoading: true,
            };
          }

          // 更新 versionPath，切换到新版本（索引 0）
          const newVersionPath = {
            ...conversation.versionPath,
            [result.retryDepth!]: 0, // 切换到新版本
          };

          // 清理 retryDepth 之后的所有 versionPath
          Object.keys(newVersionPath).forEach(key => {
            const keyDepth = parseInt(key, 10);
            if (keyDepth > result.retryDepth!) {
              delete newVersionPath[keyDepth];
            }
          });

          // 重新构建 messages
          const newMessages = rebuildMessagesWithVersionPath(
            newTreeData,
            conversation.conversationId,
            newVersionPath
          );

          return {
            ...conversation,
            treeData: newTreeData,
            versionPath: newVersionPath,
            messages: newMessages,
            isLoading: true,
          };
        })
      );
    } else {
      // 旧逻辑：没有 treeData 时的处理
      setConversations(prev =>
        prev.map(conversation => {
          if (conversation.id !== actualConversationId) {
            return conversation;
          }

          const messages = conversation.messages || [];

          // 区分 user retry 和 assistant retry
          if (message.role === 'user') {
            // User retry: 在对应的 assistant 上添加版本
            // 找到这个 user 消息的索引
            const userMsgIndex = messages.findIndex(
              msg => msg.id === message.id
            );
            if (userMsgIndex < 0) {
              return conversation;
            }

            // 找到这个 user 后面的第一个 assistant 消息
            let assistantMsgIndex = -1;
            for (let i = userMsgIndex + 1; i < messages.length; i++) {
              if (messages[i].role === 'assistant') {
                assistantMsgIndex = i;
                break;
              }
            }

            if (assistantMsgIndex < 0) {
              return conversation;
            }

            const assistantMsg = messages[assistantMsgIndex];
            const activeChoiceIndex = assistantMsg.activeChoice ?? 0;
            const currentChoice = assistantMsg.choices?.[activeChoiceIndex];

            // 创建新版本
            const newChoice = {
              version: assistantMsg.choices?.length || 0,
              blocks: currentChoice?.blocks || [],
            };

            const newChoices = [newChoice, ...(assistantMsg.choices || [])];

            return {
              ...conversation,
              messages: messages.map((msg, idx) => {
                if (idx === assistantMsgIndex) {
                  return {
                    ...msg,
                    choices: newChoices,
                    activeChoice: 0,
                    isStreaming: true,
                    multiVersion: newChoices.length,
                  };
                }
                return msg;
              }),
              isLoading: true,
            };
          } else {
            // Assistant retry: 在自己上添加版本
            return {
              ...conversation,
              messages: messages.map(msg => {
                if (msg.id === message.id) {
                  // 获取当前激活的choice
                  const activeChoiceIndex = msg.activeChoice ?? 0;
                  const currentChoice = msg.choices?.[activeChoiceIndex];

                  // 创建新版本（version字段只用于显示，不影响逻辑）
                  const newChoice = {
                    version: msg.choices?.length || 0,
                    blocks: currentChoice?.blocks || [],
                  };

                  const newChoices = [newChoice, ...(msg.choices || [])];

                  return {
                    ...msg,
                    choices: newChoices,
                    activeChoice: 0,
                    isStreaming: true, // 设置为 streaming 状态
                    multiVersion: newChoices.length, // 设置 multiVersion
                  };
                }
                return msg;
              }),
              isLoading: true,
            };
          }
        })
      );
    }

    // 延迟发送请求，使用在 prune 之前预计算好的 content 和 lastMessageId
    setTimeout(async () => {
      if (
        lastMessageIdToSend !== null &&
        typeof lastMessageIdToSend !== 'number'
      ) {
        // 🔥 关键修复：清除loading状态并移除刚创建的临时节点
        setConversations(prev =>
          prev.map(conversation => {
            if (conversation.id !== actualConversationId) {
              return conversation;
            }

            // 如果有treeData，需要移除刚创建的临时消息节点
            if (conversation.treeData && conversation.treeData.length > 0) {
              const removeNodeById = (
                nodes: any[],
                targetId: string
              ): any[] => {
                return nodes
                  .filter(node => String(node.id) !== targetId)
                  .map(node => ({
                    ...node,
                    children: node.children
                      ? removeNodeById(node.children, targetId)
                      : [],
                  }));
              };

              const cleanedTreeData = removeNodeById(
                conversation.treeData,
                pendingId // 🔥 重构：使用 pendingId
              );

              // 重新构建messages
              const newMessages = rebuildMessagesWithVersionPath(
                cleanedTreeData,
                conversation.conversationId,
                conversation.versionPath || {}
              );

              return {
                ...conversation,
                treeData: cleanedTreeData,
                messages: newMessages,
                isLoading: false,
              };
            }

            // 如果没有treeData，从messages中移除临时消息
            const cleanedMessages = (conversation.messages || []).filter(
              msg => String(msg.id) !== pendingId // 🔥 重构：统一使用 pendingId
            );

            return {
              ...conversation,
              messages: cleanedMessages,
              isLoading: false,
            };
          })
        );
        return;
      }

      // 修复：对于new chat场景，如果没有找到contentToSend，从message本身获取
      if (
        !contentToSend &&
        message.role === 'assistant' &&
        message.previousMessageId
      ) {
        // 尝试从messages数组中找到对应的user message
        const userMsg = messages.find(
          m =>
            String(m.id) === String(message.previousMessageId) ||
            m.backendId === message.previousMessageId
        );
        if (userMsg) {
          const userActiveChoiceIndex = userMsg.activeChoice ?? 0;
          contentToSend =
            userMsg.choices?.[userActiveChoiceIndex]?.blocks?.[0]?.content ||
            '';
        }
      }

      // 🔥 新增：如果还是没找到，使用之前保存的fallback content
      if (!contentToSend && fallbackContent) {
        contentToSend = fallbackContent;
        if (fallbackFileIds.length > 0) {
          fileIdsToSend = fallbackFileIds;
        }
      }

      if (!contentToSend) {
        console.error('❌ Cannot retry: no content to send');
        // 关键修复：清除loading状态并移除刚创建的临时streaming节点
        setConversations(prev =>
          prev.map(conversation => {
            if (conversation.id !== actualConversationId) {
              return conversation;
            }

            // 如果有treeData，需要移除刚创建的临时消息节点
            if (conversation.treeData && conversation.treeData.length > 0) {
              const removeNodeById = (
                nodes: any[],
                targetId: string
              ): any[] => {
                return nodes
                  .filter(node => String(node.id) !== targetId)
                  .map(node => ({
                    ...node,
                    children: node.children
                      ? removeNodeById(node.children, targetId)
                      : [],
                  }));
              };

              const cleanedTreeData = removeNodeById(
                conversation.treeData,
                pendingId // 🔥 重构：使用 pendingId
              );

              // 重新构建messages
              const newMessages = rebuildMessagesWithVersionPath(
                cleanedTreeData,
                conversation.conversationId,
                conversation.versionPath || {}
              );

              return {
                ...conversation,
                treeData: cleanedTreeData,
                messages: newMessages,
                isLoading: false,
              };
            }

            // 如果没有treeData，从messages中移除临时消息
            const cleanedMessages = (conversation.messages || []).filter(
              msg => String(msg.id) !== pendingId // 🔥 重构：统一使用 pendingId
            );

            return {
              ...conversation,
              messages: cleanedMessages,
              isLoading: false,
            };
          })
        );
        return;
      }

      await handleSendMessage(contentToSend, true, {
        lastMessageId: lastMessageIdToSend,
        pendingId, // 🔥 重构：传递 pendingId
        conversationId: actualConversationId, // 显式传递 conversationId
        fileIds: fileIdsToSend.length > 0 ? fileIdsToSend : undefined,
      });
    }, 50);
  };
  const handleEditSave = async (
    message: Message,
    newContent: string,
    fileIds?: string[]
  ) => {
    if (!currentConversationId) return;
    if (!currentConversation) {
      return;
    }
    // 获取原始内容
    const activeChoiceIndex = message.activeChoice ?? 0;
    const currentChoice = message.choices?.[activeChoiceIndex];
    const originalContent =
      currentChoice?.blocks?.find((b: any) => b.type === 'text')?.content || '';
    // 🔥 使用 fallback 模式获取文件 ID，并转换为字符串（确保类型一致）
    const originalFileIds =
      currentChoice?.blocks
        ?.filter((b: any) => b.type === 'file')
        .map((b: any) => b.fileId || b.content || b.id)
        .filter(Boolean)
        .map((id: any) => String(id)) || [];

    // 🔥 检查附件是否有变化（fileIds 已包含现有和新上传的所有文件）
    const fileIdsChanged =
      fileIds !== undefined &&
      (fileIds.length !== originalFileIds.length ||
        !fileIds.every(id => originalFileIds.includes(id)));

    // 如果内容和附件都没有改变，直接调用 handleRetry（会在当前message上增加版本）
    if (newContent.trim() === originalContent.trim() && !fileIdsChanged) {
      return handleRetry(message);
    }

    // 内容改变了，根据消息类型处理
    if (!message.backendId) {
      console.error('❌ message.backendId is undefined, cannot edit');
      // return;
    }

    // 只有user message可以编辑内容
    // 如果是assistant message，应该使用handleRetry
    if (message.role !== 'user') {
      console.warn(
        '⚠️ Cannot edit assistant message content, using retry instead'
      );
      return handleRetry(message);
    }

    // User message内容变了，在user message上增加新版本
    // 🔥 重构：生成临时消息ID，用于匹配流式返回的内容
    const pendingId = createPendingId();

    // 使用 currentConversation.id 作为真实的 conversationId
    // 避免使用可能过期的 currentConversationId
    const actualConversationId =
      currentConversation.id || currentConversationId;

    // 获取当前对话的 agent 信息（用于 client）
    const agentForRetry = currentConversationAgent || selectedAgent;
    const clientInfoForRetry = agentForRetry
      ? {
          id: agentForRetry.id,
          name: agentForRetry.name,
          logo: agentForRetry.logo,
        }
      : undefined;

    // 🔥 fileIds 已包含所有文件 ID（现有的 + 新上传的，在 MessageBubble 中上传）
    // 🔥 过滤掉 undefined 和其他 falsy 值，防止创建无效的文件块
    const allFileIds = (
      fileIds !== undefined ? fileIds : originalFileIds
    ).filter((id): id is string => Boolean(id));
    // 🔥 计算新添加的文件 ID（在 allFileIds 中但不在 originalFileIds 中）
    const newFileIds = allFileIds.filter(id => !originalFileIds.includes(id));

    // 如果有 treeData，在树中添加新版本
    if (currentConversation.treeData) {
      setConversations(prev =>
        prev.map(conversation => {
          if (conversation.id !== actualConversationId) {
            return conversation;
          }
          // 深拷贝 treeData
          const newTreeData = JSON.parse(JSON.stringify(conversation.treeData));
          // 找到要编辑的消息在树中的位置，添加新版本
          const findAndAddEditVersion = (
            nodes: any[],
            targetBackendId: number,
            depth: number,
            editedContent: string,
            tempId: string,
            originalNode: any
          ): { success: boolean; depth?: number } => {
            // 先检查当前层是否包含目标节点
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if (node.id === targetBackendId && node.role === 'user') {
                // 🔥 保留原有的非文本块（如文件块），但根据 fileIds 参数过滤
                const preservedContents = node.contents
                  .filter((c: any) => {
                    if (c.type === 'text') return false; // 排除文本块
                    if (c.type === 'file') {
                      // 如果提供了 fileIds 参数，只保留在 fileIds 中的文件
                      if (fileIds !== undefined) {
                        const contentFileId = c.fileId || c.content || c.id;
                        // 🔥 转换为字符串进行比较，避免类型不一致（数字 vs 字符串）
                        return fileIds.includes(String(contentFileId));
                      }
                    }
                    return true; // 保留其他类型的非文本块
                  })
                  .map((c: any) => ({
                    ...c,
                    createdAt: c.createdAt || new Date().toISOString(),
                  }));

                // 在当前层（nodes 数组）添加新版本
                const newUserNode = {
                  ...node,
                  id: Date.now(), // 临时 ID（user节点的ID）
                  contents: [
                    ...preservedContents, // 保留现有文件块
                    // 🔥 为新上传的文件添加文件块
                    // 同时设置 fileId 和 content，确保 convertContentsToBlocks 能正确提取
                    ...newFileIds.map(fileId => ({
                      type: 'file',
                      fileId,
                      content: fileId, // 🔥 添加 content 作为备份
                      id: fileId, // 🔥 添加 id 作为备份
                      createdAt: new Date().toISOString(),
                    })),
                    {
                      type: 'text',
                      content: editedContent,
                      createdAt: new Date().toISOString(),
                    },
                  ],
                  children: [
                    {
                      id: tempId, // 使用临时消息ID作为assistant节点的ID
                      role: 'assistant',
                      contents: [], // 空内容，等待流式填充
                      previousMessageId: node.id, // 指向原user message
                      children: [],
                      isRetry: true,
                      isStreaming: true, // 立即设置为streaming状态
                      client: clientInfoForRetry, // 保留 client 信息
                    },
                  ],
                  isRetry: true, // 标记为编辑重试节点
                };
                // 添加到当前层数组开头
                nodes.unshift(newUserNode);
                return { success: true, depth: depth + 1 }; // assistant在下一层
              }
            }
            // 如果当前层没找到，递归到每个节点的 children 中查找
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if (node.children && node.children.length > 0) {
                const result = findAndAddEditVersion(
                  node.children,
                  targetBackendId,
                  depth + 1,
                  editedContent,
                  tempId,
                  originalNode
                );
                if (result.success) return result;
              }
            }
            return { success: false };
          };
          const result = findAndAddEditVersion(
            newTreeData,
            message.backendId!,
            0,
            newContent,
            pendingId, // 🔥 重构：使用 pendingId
            message
          );
          if (!result.success) {
            console.error('❌ Failed to find message in treeData');
            return conversation;
          }

          // 找到编辑的 user 节点在树中的深度
          const findNodeDepth = (
            nodes: any[],
            targetBackendId: number,
            currentDepth: number
          ): number | null => {
            for (const node of nodes) {
              if (node.id === targetBackendId) {
                return currentDepth;
              }
              if (node.children && node.children.length > 0) {
                const found = findNodeDepth(
                  node.children,
                  targetBackendId,
                  currentDepth + 1
                );
                if (found !== null) return found;
              }
            }
            return null;
          };

          const userDepth = findNodeDepth(newTreeData, message.backendId!, 0);

          if (userDepth === null) {
            console.error('❌ Failed to find user node depth in treeData');
            return conversation;
          }

          // 更新 versionPath，切换到新版本（索引 0）
          // 清空当前深度及之后的所有路径，然后设置新路径
          const newVersionPath: Record<number, number> = {};

          // 保留当前深度之前的路径
          Object.keys(conversation.versionPath || {}).forEach(key => {
            const keyDepth = parseInt(key, 10);
            if (keyDepth < userDepth) {
              newVersionPath[keyDepth] = conversation.versionPath![keyDepth];
            }
          });

          // 设置 user 和 assistant 深度为 0（选择新节点）
          newVersionPath[userDepth] = 0; // 用户层
          newVersionPath[userDepth + 1] = 0; // 助手层（assistant 在 user 的下一层）

          // 重新构建 messages
          const newMessages = rebuildMessagesWithVersionPath(
            newTreeData,
            conversation.conversationId,
            newVersionPath
          );

          return {
            ...conversation,
            treeData: newTreeData,
            versionPath: newVersionPath,
            messages: newMessages,
            isLoading: true,
          };
        })
      );
    } else {
      setConversations(prev => {
        return prev.map(conversation => {
          if (conversation.id !== currentConversationId) {
            return conversation;
          }

          const messages = conversation.messages || [];

          // 找到被编辑的 user 消息的索引
          const userMsgIndex = messages.findIndex(
            msg => msg.id === message.id && msg.role === 'user'
          );

          if (userMsgIndex < 0) {
            return conversation;
          }

          // 找到这个 user 后面的第一个 assistant 消息
          let assistantMsgIndex = -1;
          for (let i = userMsgIndex + 1; i < messages.length; i++) {
            if (messages[i].role === 'assistant') {
              assistantMsgIndex = i;
              break;
            }
          }

          const updatedMessages = messages.map((msg, idx) => {
            // 更新user message
            if (idx === userMsgIndex) {
              // 🔥 保留原有的非文本块（如文件块），但根据 fileIds 参数过滤
              const activeChoiceIndex = msg.activeChoice ?? 0;
              const currentChoice = msg.choices?.[activeChoiceIndex];
              const preservedBlocks =
                currentChoice?.blocks?.filter((b: any) => {
                  if (b.type === 'text') return false; // 排除文本块
                  if (b.type === 'file') {
                    // 如果提供了 fileIds 参数，只保留在 fileIds 中的文件
                    if (fileIds !== undefined) {
                      const blockFileId = b.fileId || b.content || b.id;
                      return fileIds.includes(blockFileId);
                    }
                  }
                  return true; // 保留其他类型的非文本块
                }) || [];

              const newChoice = {
                version: msg.choices?.length || 0,
                blocks: [
                  ...preservedBlocks,
                  // 🔥 为新上传的文件添加文件块
                  ...newFileIds.map(fileId => ({
                    type: 'file' as const,
                    fileId,
                    content: fileId, // 🔥 添加 content 作为备份
                    id: fileId, // 🔥 添加 id 作为备份
                  })),
                  { type: 'text' as const, content: newContent },
                ],
              };
              return {
                ...msg,
                choices: [newChoice, ...(msg.choices || [])],
                blocks: newChoice.blocks,
                activeChoice: 0,
                multiVersion: [newChoice, ...(msg.choices || [])].length,
              };
            }
            // 在对应的 assistant 上添加新版本
            if (idx === assistantMsgIndex) {
              const activeChoiceIndex = msg.activeChoice ?? 0;
              const currentChoice = msg.choices?.[activeChoiceIndex];
              const newChoice = {
                version: msg.choices?.length || 0,
                blocks: currentChoice?.blocks || [],
              };
              return {
                ...msg,
                choices: [newChoice, ...(msg.choices || [])],
                activeChoice: 0,
                isStreaming: true,
                multiVersion: [newChoice, ...(msg.choices || [])].length,
              };
            }
            return msg;
          });

          return {
            ...conversation,
            messages: updatedMessages,
            isLoading: true,
          };
        });
      });
    }

    // 延迟后重新发送消息
    setTimeout(async () => {
      // Edit：lastMessageId 使用 user message 的 previousMessageId
      const lastMessageId: number | null = message.previousMessageId ?? null;

      // 🔥 文件已在前面上传完成，直接使用 allFileIds
      await handleSendMessage(newContent, true, {
        lastMessageId,
        pendingId, // 🔥 重构：传递 pendingId
        conversationId: actualConversationId, // 显式传递 conversationId
        fileIds: allFileIds.length > 0 ? allFileIds : undefined,
        clientId: agentForRetry?.id, // 传递 clientId 以保留 agent 信息
      });
    }, 50);
  };

  return {
    handleRetry,
    handleEditSave,
    setHandleSendMessage,
  };
}

export default useRetryAndEditMessage;
